import { Button } from '@/components/ui/button'
import { PersonWithPlusIcon } from '@/components/icons'
import SelectionList from './SelectionList'
import { useWizard } from '@/components/common/wizard/WizardContext'
import { cn } from '@/lib/utils'
import InlineInfo from '../../InlineInfo'
import { withoutAccountAudience } from '@/utils/account-audiences'

const DisabledStage = ({ name, showInfos }) => {
    const infoText = `${name} sales funnel stage and it's audiences are not available for Awareness campaign
                        objective.`
    return (
        <div className="tw:flex tw:gap-4 tw:flex-col">
            <div className="tw:inline-flex tw:gap-2 tw:items-center tw:px-3">
                <div
                    className={cn(
                        'tw:inline-flex tw:gap-2 tw:items-center tw:w-full tw:px-3',
                        `${name.toLowerCase()}-section`,
                    )}
                >
                    <div className="tw:size-3 tw:rounded-full tw:border-2"></div>
                    <span className="tw:font-semibold">{name}</span>
                </div>
            </div>
            {showInfos && (
                <div className="tw:mx-6">
                    <InlineInfo text={infoText} />
                </div>
            )}
        </div>
    )
}

const Stage = ({
    color,
    name,
    selectedItems,
    disabled,
    funnelStageId,
    dataChanged,
    showInfos,
    aiAudience,
    warmAudiencePanel,
    accountAudiencePanel,
    lookalike,
}) => {
    const { setStepContentData, goToStep, setVisible, getStepContentData, removeStepContentData } = useWizard()

    if (disabled) return <DisabledStage name={name} showInfos={showInfos} />

    // AI audiences and lookalikes are listed by their own panels; only the other selected audiences belong to the regular list
    const hiddenAudienceIds = aiAudience?.hiddenAudienceIds
    const hiddenLookalikeIds = lookalike?.hiddenAudienceIds
    const listItems =
        hiddenAudienceIds || hiddenLookalikeIds
            ? selectedItems.filter(
                  (audience) => !hiddenAudienceIds?.[audience.id] && !hiddenLookalikeIds?.[audience.id],
              )
            : selectedItems
    const showExistingAudiences = aiAudience?.showExistingAudiences ?? true

    const handleAttachAudience = () => {
        // Store the funnel stage in the step's data
        setStepContentData({ funnel_stage: funnelStageId }, 'attachAudience')

        // Show the step first
        setVisible('attachAudience', true)

        // Use setTimeout to ensure the state update has taken effect before navigating
        setTimeout(() => {
            goToStep('attachAudience')
        }, 0)
    }

    const handleRemoveAudience = (audienceId) => {
        // Get current sales funnel data
        const salesFunnelData = getStepContentData('salesFunnelSetup') || {}
        const audienceSelections = salesFunnelData.audienceSelections || {}

        // Remove the audience ID from this funnel stage
        const currentStageAudiences = audienceSelections[funnelStageId] || []
        const updatedStageAudiences = currentStageAudiences.filter((id) => id !== audienceId)

        // Update the wizard state
        setStepContentData(
            withoutAccountAudience(
                {
                    ...salesFunnelData,
                    audienceSelections: {
                        ...audienceSelections,
                        [funnelStageId]: updatedStageAudiences,
                    },
                },
                funnelStageId,
                audienceId,
            ),
            'salesFunnelSetup',
        )

        removeStepContentData('budgetSchedule')
        dataChanged(funnelStageId, updatedStageAudiences)
    }

    return (
        <div className="tw:flex tw:gap-4 tw:flex-col">
            <div className="tw:px-3">
                <div
                    className={cn(
                        'tw:inline-flex tw:gap-2 tw:items-center tw:w-full tw:px-3',
                        `${name.toLowerCase()}-section`,
                    )}
                >
                    <div className="tw:size-3 tw:rounded-full" style={{ backgroundColor: color }}></div>
                    <span className="tw:font-semibold">{name}</span>
                    <span className="tw:text-sm tw:text-gray-200">{selectedItems.length === 0 && 'No audience'}</span>
                </div>
            </div>
            {aiAudience?.panel}
            {warmAudiencePanel}
            {lookalike?.panel}
            {showExistingAudiences && accountAudiencePanel}
            {showExistingAudiences && listItems.length > 0 && (
                <div className="tw:flex tw:flex-col tw:border-t tw:border-b tw:divide-y">
                    <SelectionList audiences={listItems} onRemove={handleRemoveAudience} color={color} />
                </div>
            )}
            {showExistingAudiences && (
                <div className="tw:px-6">
                    <Button variant="secondary" size="sm" className="tw:w-fit" onClick={handleAttachAudience}>
                        <PersonWithPlusIcon className="tw:size-5" />
                        {listItems.length > 0 ? 'Edit Audience' : 'Attach Audience'}
                    </Button>
                </div>
            )}
        </div>
    )
}

export default Stage
